/**
 * Proximity Malt — Colorado, Delaware and Wisconsin, USA.
 *
 * Every figure here is read off the maker's own Product Guide, whose typical-
 * analysis table publishes colour, moisture, fine-grind extract, total protein
 * and diastatic power in one row per malt. Where that row is complete the entry
 * is `spec-sheet` and carries the maker's numbers unchanged.
 *
 * ## Where the table stops, and what the rest of these rows are
 *
 * The guide prints a full analysis for the base, premium and specialty malts,
 * and for two of the roasted line. For the crystal grades and the roasted malts
 * it publishes **colour and plumpness only** — no extract, no moisture, no
 * diastatic power. Those entries are marked `representative`: the colour is the
 * maker's, and the three missing figures are the median of what this catalog
 * already carries for the class (crystal 75 % / 5.5 %, roasted 70 % / 4.0 %,
 * both with no diastatic power). That is the same treatment `bestmalz.ts` gives
 * its three incomplete rows, and it beats inventing a number that would read as
 * published.
 *
 * ## The crystal line is seven grades, not a range
 *
 * Proximity sells Crystal 15, 30, 40, 60, 80, 120 and 150 as distinct products,
 * plus Caravienne 20 and Caramunich 60 which do carry a published extract. One
 * entry per grade, as this catalog does everywhere else — a single row spanning
 * "20–120 °L" would be a product line rather than something a brewer can buy.
 *
 * Colour is published in °L; the EBC alongside is the conversion
 * `EBC = 2.65 × °L − 1.2`, not a second measurement.
 *
 * @see https://proximitymalt.com/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Proximity Malt fermentables. @see Fermentable */
export const proximityFermentables: readonly Fermentable[] = [
  {
    id: 'proximity-base',
    name: 'Base Malt',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.0,
    moisturePercent: 4.5,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Proximity's workhorse North American two-row: high extract, a big enzyme package and nothing much to say for itself, which is the point of a base malt. Carries adjuncts comfortably.",
    tags: ['base', 'american', 'two-row'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-pilsen',
    name: 'Pilsen',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.0,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 145,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale, well modified and the highest-enzyme malt in the range. A clean floor under helles, pils and Kölsch, with plenty of diastatic power left over for flaked adjuncts.',
    tags: ['base', 'lager', 'pilsner', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-pale-ale',
    name: 'Pale Ale',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 3.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a shade further than the base malt for bread crust and a fuller golden wort. The default single-malt grist for American and English pale ales.',
    tags: ['base', 'pale-ale', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-super-extract-base',
    name: 'Super Extract Base',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 83.0,
    moisturePercent: 4.5,
    colorLovibond: 2.0,
    colorEbc: 4.1,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Selected and malted for yield: two points of extract above the standard base malt at the same color. Worth it where the brewhouse is the constraint rather than the flavor.',
    tags: ['base', 'high-extract', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-vienna',
    name: 'Vienna',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 3.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light amber and gently toasty, with enough enzyme to convert itself and a little besides. Vienna lager, märzen and anywhere a pale malt reads too plain.',
    tags: ['vienna', 'lager', 'toasty', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-munich-10',
    name: 'Munich 10',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 3.2,
    colorLovibond: 10.0,
    colorEbc: 25.3,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bread crust and toffee at a color you can build a whole grist from. Retains modest diastatic power, so it can carry a mash on its own.',
    tags: ['munich', 'malty', 'toasty', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-mild',
    name: 'Prox Mild',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.0,
    moisturePercent: 3.5,
    colorLovibond: 6.0,
    colorEbc: 14.7,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A soft, low-color kilned malt for milds and session bitters: rounder than pale ale malt without the toast of Munich.',
    tags: ['mild', 'english', 'session', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-red',
    name: 'Prox Red',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.0,
    moisturePercent: 3.7,
    colorLovibond: 18.0,
    colorEbc: 46.5,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to a deep copper. Red ales, altbiers and amber lagers, where the color has to arrive without the roast that would come with crystal malt.',
    tags: ['red', 'amber', 'altbier', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-mid-atlantic-wheat',
    name: 'Mid-Atlantic Wheat',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85.0,
    moisturePercent: 5.5,
    colorLovibond: 3.0,
    colorEbc: 6.7,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat grown and malted on the eastern seaboard. High extract and high protein for head retention, body and haze.',
    tags: ['wheat', 'head-retention', 'regional', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85.0,
    moisturePercent: 5.0,
    colorLovibond: 3.0,
    colorEbc: 6.7,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Soft white wheat, malted. Creamy body and a lasting head; the base of an American wheat beer and the standard haze contributor in a New England IPA.',
    tags: ['wheat', 'hazy', 'head-retention', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-rye',
    name: 'Rye Malt',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82.0,
    moisturePercent: 4.5,
    colorLovibond: 4.0,
    colorEbc: 9.4,
    diastaticPowerLintner: 100,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Earthy, peppery rye with enough enzyme to convert itself. Builds a slick, dry body that suits roggenbier and rye IPA.',
    tags: ['rye', 'spicy', 'mouthfeel', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-oat',
    name: 'Oat Malt',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 65.0,
    moisturePercent: 5.0,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats taken through a full malting rather than flaked, so they bring some enzyme of their own. Silky mouthfeel for stouts and hazy IPA.',
    tags: ['oats', 'hazy', 'mouthfeel', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-caravienne-20',
    name: 'Caravienne 20',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 3.5,
    colorLovibond: 20.0,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light stewed crystal: honey and light toffee, and a golden-amber lift without much sweetness. Belgian pales and dubbels.',
    tags: ['crystal', 'belgian', 'toffee', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-caramunich-60',
    name: 'Caramunich 60',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 3.5,
    colorLovibond: 60.0,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark caramel, raisin and a copper-red cast. The crystal malt behind a Belgian dubbel or a malt-forward amber.',
    tags: ['crystal', 'belgian', 'dubbel', 'american'],
    provenance: 'spec-sheet',
  },
  {
    id: 'proximity-crystal-15',
    name: 'Crystal 15',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 15.0,
    colorEbc: 38.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest of the crystal line: candy-like sweetness and a golden lift, with almost no caramel of its own.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-30',
    name: 'Crystal 30',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 30.0,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description: 'Light caramel and honey, still pale enough to leave a blonde beer blonde.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-40',
    name: 'Crystal 40',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 40.0,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description: 'Golden toffee and a rounder body. The default crystal in an American pale ale.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-60',
    name: 'Crystal 60',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 60.0,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The classic middle grade: distinct caramel and toffee, with red and gold color tones.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-80',
    name: 'Crystal 80',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 80.0,
    colorEbc: 210.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description: 'Deeper caramel with the first hint of dark fruit, and a noticeably red cast.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-120',
    name: 'Crystal 120',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 120.0,
    colorEbc: 316.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark caramel and burnt sugar with raisin complexity. Color and flavor without dominating the grist.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-crystal-150',
    name: 'Crystal 150',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.0,
    moisturePercent: 5.5,
    colorLovibond: 150.0,
    colorEbc: 396.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest crystal before the chocolates begin: figs, prunes and a cocoa finish.',
    tags: ['crystal', 'caramel', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-biscuit',
    name: 'Biscuit',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 20.0,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dry-roasted to a biscuit: toasted bread and a nutty finish, with no crystal sweetness behind it.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-pale-chocolate',
    name: 'Pale Chocolate',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 300.0,
    colorEbc: 793.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Cocoa and light coffee, gentler than full chocolate malt. Porters and brown ales that want the flavor without the color.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-chocolate',
    name: 'Chocolate',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 350.0,
    colorEbc: 926.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Bittersweet cocoa and espresso, smooth rather than sharp. The backbone of a porter.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-dark-chocolate',
    name: 'Dark Chocolate',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 400.0,
    colorEbc: 1058.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Deeper and drier than standard chocolate malt, with the roast pushed toward burnt cocoa.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-black',
    name: 'Black',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 500.0,
    colorEbc: 1323.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A sharp, clean black. Used in small amounts for color, and in slightly larger ones for the acrid edge a schwarzbier wants.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-pale-roasted-barley',
    name: 'Pale Roasted Barley',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 325.0,
    colorEbc: 860.0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted to a light coffee. The dry-stout character at a color that leaves the beer brown rather than black.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
  {
    id: 'proximity-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Proximity Malt',
    origin: 'United States',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70.0,
    moisturePercent: 4.0,
    colorLovibond: 475.0,
    colorEbc: 1257.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken to espresso. The defining grain of a dry Irish stout: acrid, dry and unmistakable.',
    tags: ['roasted', 'dark', 'american'],
    provenance: 'representative',
  },
];
